import * as B from '@blueprintjs/core';
import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import classnames from 'classnames';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';
import {StringParam, useQueryParam} from 'use-query-params';

import {getBillableAcres} from 'app/components/OrderImageryModal/orderImageryUtils';
import CartPaymentElement from 'app/components/PaymentMethods/CartPaymentElement';
import type {
  CartPaymentHandle,
  CartPaymentStatus,
} from 'app/components/PaymentMethods/CartPaymentElement';
import {useCartCustomerSession} from 'app/components/PaymentMethods/hooks';
import {
  CHALLENGE_UNAVAILABLE_MESSAGE,
  CartItemSnapshot,
  CartItemV1,
  MAX_CART_ITEMS,
  asksForCard,
  cartItemDeepLink,
  cartItemPropertyLink,
  cartItemSubsetFeature,
  cartTotalAcres,
  cartTotalCents,
  chargedCents,
  checkoutAttemptStore,
  checkoutContents,
  checkoutOptions,
  checkoutOutcome,
  checkoutPreviewQuery,
  creditBalanceOf,
  dedupeNotices,
  groupCartItems,
  invalidateCheckoutCredit,
  isPayNowEnabled,
  normalizeCheckoutFailure,
  outdatesCredit,
  paymentNotice,
  planCartPayment,
  prepaidRemainingCents,
  propertyGroupingAvailable,
  resolveCartGroupBy,
  settleCheckout,
  sleep,
  submitAttempt,
  toCheckoutItem,
  useCart,
  useCheckoutPrepaids,
  useRemoveWithUndo,
  withCardRequest,
} from 'app/modules/Cart';
import type {CartPaymentPlan, ChargeGroup, CheckoutAttempt, PaymentNotice} from 'app/modules/Cart';
import {lookUpFeatures, lookUpScenes, sceneKeyOf, sceneKeysFor} from 'app/modules/Cart/sceneLookup';
import {api} from 'app/modules/Remote';
import type {ApiImageryCheckoutItemError} from 'app/modules/Remote/api';
import {ApiFeature, ApiOrderableScene} from 'app/modules/Remote/Feature';
import {ApiImageryPrices} from 'app/modules/Remote/Imagery';
import {ApiImageryContract} from 'app/modules/Remote/Project';
import {useUserInfo} from 'app/providers/AuthProvider';
import {
  invalidateImageryBudgets,
  useCurrentImageryBudget,
} from 'app/providers/ImageryContractProvider';
import {recordEvent} from 'app/tools/Analytics';
import {INERT} from 'app/utils/domUtils';
import * as featureUtils from 'app/utils/featureUtils';
import {getThumbnailOverrideScene} from 'app/utils/imageryUtils';
import * as routeUtils from 'app/utils/routeUtils';

import CartPageView from './CartPageView';
import CheckoutSuccess from './CheckoutSuccess';
import {
  BudgetLookupEntry,
  CartContractGroup,
  ContractLookupEntry,
  OrderedGroupBy,
  groupCartItemsByContract,
  nteBlockMessage,
  portfolioNames,
  usageAndLimits,
} from './grouping';
import {featureTargetKey, featureTargetsFor} from './refresh';
import cs from './styles.module.scss';
import type {
  AcceptedQuote,
  CartItemPreview,
  CartItemQuote,
  CheckoutGroupResult,
  ItemFacts,
  ItemRejections,
  ItemValidation,
  SubmitBlock,
} from './types';
import {
  acceptedQuoteFrom,
  applyAcceptedQuote,
  failedResultStillApplies,
  groupFailures,
  joinOrderedItems,
  planFixes,
  reduceItemValidation,
  rejectionsByItemId,
  rejectionsInCart,
  requotePatch,
  unmatchedErrors,
} from './validation';

interface ItemShape {
  id: string;
  projectId: string;
  featureCollectionId: number;
  featureId: number;
  sourceId: string;
  sensingTime: string;
  geometry?: geojson.Polygon | geojson.MultiPolygon;
}

interface FeatureRefresh {
  /** Null only on a 404: the feature is gone, which is a real per-row answer. */
  feature: I.ImmutableOf<ApiFeature> | null;
  scenes: I.List<I.ImmutableOf<ApiOrderableScene>>;
}

const sceneFor = (refresh: FeatureRefresh, shape: ItemShape) =>
  refresh.scenes.find(
    (candidate) =>
      candidate?.get('sourceId') === shape.sourceId &&
      candidate.get('sensingTime') === shape.sensingTime
  );

const itemShapeSignature = (item: ItemShape) =>
  [
    item.id,
    item.projectId,
    item.featureCollectionId,
    item.featureId,
    item.sourceId,
    item.sensingTime,
    item.geometry ? JSON.stringify(item.geometry) : '',
  ].join('~');

const groupContents = (group: CartContractGroup) =>
  checkoutContents(group.items.map(toCheckoutItem));

export const CartPage: React.FC = () => {
  const {items, clear, removeItem, removeItems, updateItem, updateItems, scopeKey} = useCart();
  const removeWithUndo = useRemoveWithUndo();
  const [organization, profile] = useUserInfo();
  const organizationId = organization.get('id');
  const payNow = isPayNowEnabled(organization, profile);
  const orgIdPrefix = routeUtils.useCurrentOrgIdPrefix();
  const queryClient = useQueryClient();

  const [groupByParam, setGroupByParam] = useQueryParam('groupBy', StringParam);
  const propertyGroupingIsAvailable = React.useMemo(
    () => propertyGroupingAvailable(items),
    [items]
  );
  const groupBy = resolveCartGroupBy(groupByParam, propertyGroupingIsAvailable);
  const [orderedResults, setOrderedResults] = React.useState<CheckoutGroupResult[]>([]);
  // When the latest order went through. The balance fetched before then still counts its credit.
  const [lastOrderedAt, setLastOrderedAt] = React.useState<number | null>(null);
  const [failedResults, setFailedResults] = React.useState<CheckoutGroupResult[]>([]);
  const [acceptedQuotes, setAcceptedQuotes] = React.useState<ReadonlyMap<string, AcceptedQuote>>(
    new Map()
  );
  const [rejections, setRejections] = React.useState<
    ReadonlyMap<string, ApiImageryCheckoutItemError>
  >(new Map());
  const [paymentNotices, setPaymentNotices] = React.useState<PaymentNotice[]>([]);
  // Set from the Submit click until the server answers, so the card's tokenizing reads as busy too.
  // `plan` is the payment plan as it was on the click: a submit records each group's attempt before
  // the request goes out, and a waiting attempt otherwise reads as one whose credit may already be
  // spent, so the totals would change under the buyer mid-order. `isFinishingOrder` means a sibling
  // request still holds a group's key, and the submit is waiting for it to finish.
  const [submitting, setSubmitting] = React.useState<{
    plan: CartPaymentPlan | null;
    isFinishingOrder: boolean;
  } | null>(null);
  const [billingNote, setBillingNote] = React.useState('');

  // Set by the mounted PaymentElement, which is the only thing that can reach Stripe's objects.
  const paymentHandleRef = React.useRef<CartPaymentHandle | null>(null);
  const [paymentStatus, setPaymentStatus] = React.useState<CartPaymentStatus>('loading');
  // Shares React Query's cache with the element; read here for whether Submit can go through.
  const customerSessionQuery = useCartCustomerSession(organizationId, payNow);

  const liveRejections: ItemRejections = React.useMemo(
    () => rejectionsInCart(items, rejections),
    [items, rejections]
  );

  // Row identity and sub-area, which is everything the refresh pass fetches against. Writing a
  // re-quote back doesn't touch any of it, so the expensive acreage math below isn't redone for it.
  const shapeSignature = items.map(itemShapeSignature).join('|');
  const itemShapes: ItemShape[] = React.useMemo(
    () =>
      items.map(
        ({id, projectId, featureCollectionId, featureId, sourceId, sensingTime, geometry}) => ({
          id,
          projectId,
          featureCollectionId,
          featureId,
          sourceId,
          sensingTime,
          geometry,
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shapeSignature]
  );

  const projectIds = React.useMemo(
    () => [...new Set(itemShapes.map((shape) => shape.projectId))],
    [itemShapes]
  );

  const featureTargets = React.useMemo(() => featureTargetsFor(itemShapes), [itemShapes]);

  const pricesQuery = useQuery({
    queryKey: ['cartImageryPrices'],
    queryFn: async () =>
      (await api.imagery.prices.fetch()).get('data').toJS() as unknown as ApiImageryPrices,
  });

  // An org that pays at checkout bills no contract, so it has none to look up.
  const contractProjectIds = React.useMemo(() => (payNow ? [] : projectIds), [payNow, projectIds]);
  const contractQueries = useQueries({
    queries: contractProjectIds.map((projectId) => ({
      queryKey: ['cartImageryContract', projectId],
      structuralSharing: false,
      queryFn: async (): Promise<ApiImageryContract | null> => {
        const data = (await api.projects.currentImageryContract(projectId)).get('data');
        return data ? (data.toJS() as ApiImageryContract) : null;
      },
    })),
  });

  const budgetQuery = useCurrentImageryBudget(organizationId);

  const sceneKeys = React.useMemo(() => sceneKeysFor(itemShapes), [itemShapes]);

  // The whole cart is checked in two lookups, its scenes and its properties, rather than one per
  // property. A refetch keeps the last answer on screen, and a row only uses it if it covered the row.
  // Both hand Immutable records straight to getBillableAcres; React Query's structural sharing walks
  // plain objects and would rebuild them as POJOs.
  const scenesQuery = useQuery({
    queryKey: ['cartScenes', sceneKeys],
    queryFn: () => lookUpScenes(sceneKeys),
    structuralSharing: false,
    placeholderData: keepPreviousData,
  });
  const featuresQuery = useQuery({
    queryKey: [
      'cartFeatures',
      featureTargets.map(({featureCollectionId, featureId}) => [featureCollectionId, featureId]),
    ],
    queryFn: () => lookUpFeatures(featureTargets),
    structuralSharing: false,
    placeholderData: keepPreviousData,
  });

  // useQueries hands back a fresh array every render, so this stands in for it in dependency lists.
  const contractSignature = contractQueries
    .map((query) => `${query.status}:${query.dataUpdatedAt}`)
    .join('|');

  const refreshFailed =
    pricesQuery.isError ||
    contractQueries.some((query) => query.isError) ||
    scenesQuery.isError ||
    featuresQuery.isError;

  const contractLookup: ReadonlyMap<string, ContractLookupEntry> = React.useMemo(() => {
    const map = new Map<string, ContractLookupEntry>();
    contractProjectIds.forEach((projectId, index) => {
      const query = contractQueries[index];
      map.set(projectId, {contract: query?.data ?? null, isLoading: query?.isPending ?? true});
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractProjectIds, contractSignature]);

  const budgetLookup: BudgetLookupEntry = React.useMemo(
    // Not settled until it has loaded: falling back to the contract's cap on an error would check
    // the wrong limit for an organization on budgets. A failed refetch keeps the budget it had.
    () => ({budget: budgetQuery.data ?? null, isLoading: budgetQuery.data === undefined}),
    [budgetQuery.data]
  );

  const prices = pricesQuery.data;

  // Keyed by row id, and invalidated when the row's property, scene or sub-area changes: the lookups
  // refetch the whole cart together, and most rows come back unchanged.
  const acresCacheRef = React.useRef(
    new Map<string, {signature: string; acres: ItemFacts['acres']}>()
  );

  const refreshByFeature = React.useMemo(() => {
    const scenes = scenesQuery.data;
    const features = featuresQuery.data;
    const map = new Map<string, FeatureRefresh | undefined>();
    for (const target of featureTargets) {
      const covered =
        scenes !== undefined &&
        features !== undefined &&
        features.asked.has(target.featureId) &&
        sceneKeys.every(
          (key) => key.featureId !== target.featureId || scenes.asked.has(sceneKeyOf(key))
        );
      map.set(
        featureTargetKey(target),
        covered
          ? {
              feature: features.featuresById.get(target.featureId) ?? null,
              scenes: scenes.scenesByFeature.get(target.featureId) ?? I.List(),
            }
          : undefined
      );
    }
    return map;
  }, [featureTargets, sceneKeys, scenesQuery.data, featuresQuery.data]);

  const previews: ReadonlyMap<string, CartItemPreview> = React.useMemo(() => {
    const map = new Map<string, CartItemPreview>();
    for (const shape of itemShapes) {
      const refresh = refreshByFeature.get(featureTargetKey(shape));
      if (!refresh?.feature) continue;
      const scene = sceneFor(refresh, shape);
      if (scene) {
        map.set(shape.id, {feature: refresh.feature, scene, orderableScenes: refresh.scenes});
      }
    }
    return map;
  }, [itemShapes, refreshByFeature]);

  const facts: ReadonlyMap<string, ItemFacts> = React.useMemo(() => {
    const nextAcresCache = new Map<string, {signature: string; acres: ItemFacts['acres']}>();
    const map = new Map<string, ItemFacts>();
    for (const shape of itemShapes) {
      const featureKey = featureTargetKey(shape);
      const refresh = refreshByFeature.get(featureKey);
      const scene = refresh === undefined ? undefined : (sceneFor(refresh, shape) ?? null);

      let acres: ItemFacts['acres'];
      let snapshot: Partial<CartItemSnapshot> | undefined;
      if (refresh?.feature && scene) {
        // Immutable caches a record's hash, so an unchanged property and scene keep their acres across refetches.
        const rowSignature = `${refresh.feature.hashCode()}:${scene.hashCode()}::${itemShapeSignature(shape)}`;
        const cached = acresCacheRef.current.get(shape.id);
        if (cached && cached.signature === rowSignature) {
          acres = cached.acres;
        } else {
          try {
            acres = getBillableAcres(refresh.feature, scene, cartItemSubsetFeature(shape));
          } catch {
            // One unusable geometry blocks its own row rather than taking the whole page down.
            acres = {orderGeometryAcres: -1, billableAcres: -1};
          }
        }
        nextAcresCache.set(shape.id, {signature: rowSignature, acres});

        const {url, sourceDetails} = featureUtils.imageAndSourceDetailsFromScene(scene);
        // A multispectral scene has no preview of its own, so its truecolor sibling stands in.
        const overrideScene = getThumbnailOverrideScene(scene, refresh.scenes);
        const thumbnailUrl = overrideScene
          ? featureUtils.imageAndSourceDetailsFromScene(overrideScene).url
          : url;
        snapshot = {
          featureName: refresh.feature.getIn(['properties', 'name']) as string,
          thumbnailUrl: thumbnailUrl ?? null,
          thumbnailIsOverride: overrideScene !== undefined,
          ...(sourceDetails
            ? {sourceDisplayName: `${sourceDetails.operator} ${sourceDetails.name}`}
            : {}),
        };
      } else if (scene === null) {
        acres = null;
      }

      map.set(shape.id, {
        centsPerAcre: prices === undefined ? undefined : (prices[shape.sourceId] ?? null),
        scene: scene ? (scene.toJS() as ApiOrderableScene) : scene,
        acres,
        snapshot,
        featureMissing: refresh !== undefined && refresh.feature === null,
      });
    }
    acresCacheRef.current = nextAcresCache;
    return map;
  }, [itemShapes, refreshByFeature, prices]);

  // The quote each row arrived with. Kept so a row still reads "$1.50 → $1.60" after the new price
  // has been written back to storage.
  const originalQuotesRef = React.useRef(new Map<string, CartItemQuote>());
  const originalQuotes: ReadonlyMap<string, CartItemQuote> = React.useMemo(() => {
    const next = new Map<string, CartItemQuote>();
    for (const item of items) {
      next.set(
        item.id,
        originalQuotesRef.current.get(item.id) ?? {
          centsPerAcre: item.centsPerAcre,
          billableAcres: item.billableAcres,
        }
      );
    }
    originalQuotesRef.current = next;
    return next;
  }, [items]);

  const quotedFacts: ReadonlyMap<string, ItemFacts> = React.useMemo(() => {
    if (acceptedQuotes.size === 0) {
      return facts;
    }
    const map = new Map(facts);
    for (const [id, accepted] of acceptedQuotes) {
      const base = map.get(id);
      if (base) {
        map.set(id, applyAcceptedQuote(base, accepted));
      }
    }
    return map;
  }, [facts, acceptedQuotes]);

  const validations: ReadonlyMap<string, ItemValidation> = React.useMemo(() => {
    const map = new Map<string, ItemValidation>();
    for (const item of items) {
      const quote = originalQuotes.get(item.id);
      const itemFacts = quotedFacts.get(item.id);
      if (quote && itemFacts) {
        map.set(item.id, reduceItemValidation(item, quote, itemFacts));
      }
    }
    return map;
  }, [items, originalQuotes, quotedFacts]);

  // Each patch is a diff against what's stored, so once written the next pass produces none and this
  // settles after one extra render.
  React.useEffect(() => {
    const patches = [...validations].flatMap(([id, validation]) =>
      validation.patch ? [{id, patch: validation.patch}] : []
    );
    if (patches.length > 0) {
      updateItems(patches);
    }
  }, [validations, updateItems]);

  // Contract grouping is what bills, whatever the user is looking at: the endpoint refuses a
  // checkout spanning contracts, so submit and every gate below read these groups, not the view's.
  const contractGroups = React.useMemo(
    () => groupCartItemsByContract(items, contractLookup, budgetLookup, payNow),
    [items, contractLookup, budgetLookup, payNow]
  );

  const displayGroups = React.useMemo(() => groupCartItems(items, groupBy), [items, groupBy]);

  // Each group is its own charge, and Stripe prices each one: its fee and its tax.
  const pricedGroups = payNow ? contractGroups : [];
  const chargePreviews = useQueries({
    queries: pricedGroups.map((group) => checkoutPreviewQuery(organizationId, group.items)),
  });
  const prepaidsQuery = useCheckoutPrepaids(organizationId, payNow);

  // A group's checkout attempt has to survive a retry whole. The key is what every Stripe
  // idempotency key behind the group's invoice is built from, and the server's single confirm is
  // keyed on the pair — so resending both is what finishes a charge that hasn't settled, while a
  // fresh key would raise a second invoice and charge the card again. It's dropped once the server
  // is done with it: the order committed, the card was declined, or the cart was turned down.
  const attemptStore = React.useMemo(() => checkoutAttemptStore(scopeKey), [scopeKey]);

  const chargeGroups: ChargeGroup[] = contractGroups.map((group) => {
    const previewIndex = pricedGroups.indexOf(group);
    return {
      key: group.key,
      contents: groupContents(group),
      subtotalCents: group.subtotalCents,
      isLoading: group.nte.kind === 'loading',
      waiting: attemptStore.get(group.key),
      preview: previewIndex === -1 ? undefined : chargePreviews[previewIndex],
    };
  });

  // One submit gives every group the same note, so an attempt still waiting to be resent fixes it
  // for the whole cart: the note is among the params behind that attempt's invoice, and a resend
  // carrying a different one is refused.
  const heldAttempt = chargeGroups.find((group) => group.waiting)?.waiting;
  const noteInUse = heldAttempt ? (heldAttempt.note ?? '') : billingNote;

  // By group, the order the last answer asked a card for. Only a failure gives up a key, and an ask
  // that keeps its key is also recorded on its stored attempt, which outlives the page.
  const cardRequests = React.useMemo(
    () =>
      new Map(
        failedResults.flatMap((result): [string, string][] =>
          asksForCard(result.outcome)
            ? [[result.groupKey, checkoutContents(result.submitted.map(toCheckoutItem))]]
            : []
        )
      ),
    [failedResults]
  );

  const livePlan = payNow
    ? planCartPayment({
        groups: chargeGroups,
        cardRequests,
        balance: creditBalanceOf(prepaidsQuery),
        budgetFailed: budgetQuery.isLoadingError,
      })
    : null;
  const plan = submitting ? submitting.plan : livePlan;
  const isFinishingOrder = submitting?.isFinishingOrder ?? false;
  const groupPayments = [...(plan?.payments.values() ?? [])];
  const charges = plan?.charges ?? null;
  const chargesStatus = charges?.status;
  const paysWithCredit =
    groupPayments.length > 0 && groupPayments.every((payment) => payment === 'credit');
  const showsCardForm = groupPayments.includes('card');
  // Until the balance and the prices say whether credit covers the cart, the card form is held
  // back, so it can't flash up and go.
  const isCheckingCredit = groupPayments.includes('checking');

  const runChallenge = async (clientSecret: string): Promise<string | null> => {
    const handle = paymentHandleRef.current;
    return handle ? handle.handleNextAction(clientSecret) : CHALLENGE_UNAVAILABLE_MESSAGE;
  };

  const submitMutation = useMutation({
    mutationFn: async ({
      groups: toSubmit,
      payment,
    }: {
      groups: CartContractGroup[];
      /** Keyed by group; absent when the cart bills its contract rather than a card. */
      payment?: Map<string, CheckoutAttempt>;
    }): Promise<{
      results: CheckoutGroupResult[];
      notices: PaymentNotice[];
      attempts: Map<string, CheckoutAttempt | null>;
    }> => {
      const checkout = async (group: CartContractGroup): Promise<CheckoutGroupResult> => {
        const submitted = group.items;
        const identity = {groupKey: group.key, label: group.label, submitted};
        const attempt = payment?.get(group.key);
        try {
          const result = await api.imagery.checkouts.create(
            submitted.map(toCheckoutItem),
            'cart',
            attempt && checkoutOptions(attempt)
          );
          return {...identity, outcome: checkoutOutcome(result)};
        } catch (thrown) {
          return {
            ...identity,
            outcome: {kind: 'failure', failure: normalizeCheckoutFailure(thrown, submitted)},
          };
        }
      };

      // One call per contract group: the endpoint refuses a checkout spanning contracts, so groups
      // succeed or fail on their own.
      const results = await Promise.all(toSubmit.map(checkout));

      // Sequential, not Promise.all: a 3D Secure challenge opens a Stripe modal over the page, and
      // two of those can't be up at once.
      const settled: CheckoutGroupResult[] = [];
      const attempts = new Map<string, CheckoutAttempt | null>();
      for (const [index, result] of results.entries()) {
        const group = toSubmit[index];
        const attempt = payment?.get(group.key);
        if (!attempt) {
          settled.push(result);
          continue;
        }
        const {outcome, attempt: next} = await settleCheckout(attempt, result.outcome, {
          resend: async () => (await checkout(group)).outcome,
          challenge: runChallenge,
          wait: (ms, reason) => {
            if (reason === 'inFlight') {
              setSubmitting((current) => current && {...current, isFinishingOrder: true});
            }
            return sleep(ms);
          },
        });
        settled.push({...result, outcome});
        attempts.set(group.key, withCardRequest(next, outcome, groupContents(group)));
      }

      const notices = settled.flatMap((result) =>
        result.outcome.kind === 'unpaid'
          ? (paymentNotice(result.outcome.payment, result.outcome.actionError ?? null) ?? [])
          : []
      );

      return {results: settled, notices, attempts};
    },
    onSuccess: ({results, notices, attempts}) => {
      const succeeded = results.filter((result) => result.outcome.kind === 'success');
      const failed = results.filter((result) => result.outcome.kind === 'failure');
      if (succeeded.length > 0) {
        setLastOrderedAt(Date.now());
      }

      for (const [groupKey, attempt] of attempts) {
        if (attempt) {
          attemptStore.set(groupKey, attempt);
        } else {
          attemptStore.delete(groupKey);
        }
      }

      setOrderedResults((previous) => [...previous, ...succeeded]);
      setFailedResults(failed);
      setPaymentNotices(dedupeNotices(notices));
      setRejections(rejectionsByItemId(failed));
      removeItems(succeeded.flatMap((result) => result.submitted.map((item) => item.id)));
      // A note is for one order. Anything short of the whole cart going through keeps it, since
      // the rows left behind are still that order.
      if (succeeded.length === results.length) {
        setBillingNote('');
      }
      if (results.some((result) => outdatesCredit(result.outcome))) {
        void invalidateCheckoutCredit(queryClient);
      }

      for (const result of succeeded) {
        for (const item of result.submitted) {
          // Also what marks the "Order an image" onboarding task done.
          recordEvent('Ordered an image', {
            projectId: item.projectId,
            featureCollectionId: item.featureCollectionId,
            featureId: item.featureId,
            sourceId: item.sourceId,
            sensingTime: item.sensingTime,
          });
          queryClient.invalidateQueries({
            queryKey: ['cartFeature', item.projectId, item.featureCollectionId, item.featureId],
          });
        }
      }
      const refusedOverCap = failed.some(
        (result) =>
          result.outcome.kind === 'failure' && result.outcome.failure.kind === 'nteExceeded'
      );
      if (succeeded.length > 0 || refusedOverCap) {
        invalidateImageryBudgets(queryClient);
      }
    },
    onSettled: () => setSubmitting(null),
  });

  const dropRejection = (itemId: string) =>
    setRejections((previous) => {
      const next = new Map(previous);
      next.delete(itemId);
      return next;
    });

  const onRemoveBlocked = (item: CartItemV1) => removeItem(item.id);

  const onClearCart = () => {
    clear();
    setRejections(new Map());
    setFailedResults([]);
    setOrderedResults([]);
    setPaymentNotices([]);
  };

  const rememberAcceptedQuotes = (entries: {itemId: string; quote: AcceptedQuote}[]) => {
    if (entries.length === 0) {
      return;
    }
    setAcceptedQuotes((previous) => {
      const next = new Map(previous);
      for (const {itemId, quote} of entries) {
        next.set(itemId, {...next.get(itemId), ...quote});
      }
      return next;
    });
  };

  const acceptedQuoteEntry = (itemId: string) => {
    const rejection = rejections.get(itemId);
    const quote = rejection && acceptedQuoteFrom(rejection, facts.get(itemId));
    return quote ? [{itemId, quote}] : [];
  };

  const onAcceptRequote = (item: CartItemV1) => {
    const rejection = rejections.get(item.id);
    const patch = rejection && requotePatch(rejection);
    if (patch) {
      rememberAcceptedQuotes(acceptedQuoteEntry(item.id));
      updateItem(item.id, patch);
    }
    dropRejection(item.id);
  };

  const fixPlan = React.useMemo(
    () => planFixes(items, validations, liveRejections),
    [items, validations, liveRejections]
  );

  const onApplyAllFixes = () => {
    rememberAcceptedQuotes(fixPlan.requotes.flatMap(({itemId}) => acceptedQuoteEntry(itemId)));
    removeItems(fixPlan.removeItemIds);
    updateItems(fixPlan.requotes.map(({itemId, patch}) => ({id: itemId, patch})));
    setRejections((previous) => {
      const next = new Map(previous);
      fixPlan.requotes.forEach(({itemId}) => next.delete(itemId));
      return next;
    });
  };

  const isValidating = [...validations.values()].some(
    (validation) => validation.status.kind === 'validating'
  );

  const submitBlock = React.useMemo((): SubmitBlock | null => {
    const problem = (message: string): SubmitBlock => ({tone: 'problem', message});
    const progress = (message: string): SubmitBlock => ({tone: 'progress', message});

    if (isFinishingOrder) {
      return progress('Finishing your order…');
    }
    if (items.length === 0) {
      return problem('Your cart is empty.');
    }
    if (items.length > MAX_CART_ITEMS) {
      return problem(
        `You can order up to ${MAX_CART_ITEMS} scenes at a time. Remove some to continue.`
      );
    }
    if (refreshFailed) {
      return problem('We couldn’t check every scene. Reload the page and try again.');
    }
    if (budgetQuery.isLoadingError) {
      return problem(
        'We couldn’t check the funds remaining for this order. Reload the page and try again.'
      );
    }
    if (isValidating) {
      return progress('Checking prices and availability…');
    }
    if (liveRejections.size > 0) {
      // A request with a rejected scene orders nothing, but the cart submits one request per
      // portfolio, so a sibling portfolio's scenes may already have gone through.
      const somethingOrdered = orderedResults.some((result) => result.outcome.kind === 'success');
      return problem(
        `${somethingOrdered ? 'The rest of your order' : 'Your order'} did not go through. Fix the indicated scenes before trying again.`
      );
    }
    if ([...validations.values()].some((validation) => validation.status.kind === 'unorderable')) {
      return problem('Remove the scenes that can’t be ordered.');
    }
    // Customers know their portfolios, not the contracts behind them, so a block names portfolios.
    const noContract = contractGroups.find((group) => group.nte.kind === 'noContract');
    if (noContract) {
      return problem(
        `Ordering isn’t available for ${portfolioNames(noContract)}. Remove those scenes to continue.`
      );
    }
    const nteBlock = nteBlockMessage(contractGroups);
    if (nteBlock) {
      return problem(nteBlock);
    }
    if (contractGroups.some((group) => group.nte.kind === 'loading')) {
      return progress('Checking remaining funds…');
    }
    if (payNow) {
      // Every pay-now checkout bills the organization's Stripe customer, card or not.
      if (customerSessionQuery.data?.kind === 'notConfigured') {
        return problem('Billing isn’t set up for this organization yet. Contact support.');
      }
      if (isCheckingCredit) {
        // Until the balance says there's credit to check, this reads as the card form loading,
        // which is what it is for an organization with none.
        return progress(
          prepaidsQuery.isPending ? 'Loading payment options…' : 'Checking your prepaid credit…'
        );
      }
      // Only the card form needs a customer session.
      if (showsCardForm) {
        if (customerSessionQuery.isError || paymentStatus === 'failed') {
          return problem('Payment options didn’t load.');
        }
        if (customerSessionQuery.isPending || paymentStatus === 'loading') {
          return progress('Loading payment options…');
        }
      }
      // The buyer has to see what the card will be charged, fee and tax included, before paying.
      if (chargesStatus === 'loading') {
        return progress('Calculating tax…');
      }
      if (chargesStatus === 'failed') {
        return problem('We couldn’t calculate tax for this order. Reload the page to try again.');
      }
    }
    return null;
  }, [
    items,
    refreshFailed,
    budgetQuery.isLoadingError,
    isValidating,
    liveRejections.size,
    validations,
    contractGroups,
    orderedResults,
    payNow,
    isCheckingCredit,
    prepaidsQuery.isPending,
    showsCardForm,
    chargesStatus,
    customerSessionQuery.isPending,
    customerSessionQuery.isError,
    customerSessionQuery.data,
    paymentStatus,
    isFinishingOrder,
  ]);

  const liveFailures = React.useMemo(() => {
    const byKey = new Map(contractGroups.map((group) => [group.key, group]));
    return failedResults.filter((result) =>
      failedResultStillApplies(result, byKey.get(result.groupKey), liveRejections)
    );
  }, [failedResults, contractGroups, liveRejections]);

  const orderedLines = React.useMemo(
    () =>
      orderedResults.flatMap((result) =>
        result.outcome.kind === 'success'
          ? joinOrderedItems(result.outcome.result, result.submitted)
          : []
      ),
    [orderedResults]
  );
  const orderedTotals = React.useMemo(
    () =>
      orderedResults.reduce(
        (totals, result) => {
          if (result.outcome.kind !== 'success') {
            return totals;
          }
          const charged = chargedCents(result.outcome.result);
          return {
            cents: totals.cents + charged.cents,
            taxCents: totals.taxCents + charged.taxCents,
            acres: totals.acres + result.outcome.result.totalBilledAcres,
            prepaidCents: totals.prepaidCents + (result.outcome.result.prepaidCents ?? 0),
          };
        },
        {cents: 0, taxCents: 0, acres: 0, prepaidCents: 0}
      ),
    [orderedResults]
  );
  // Only a balance fetched after the latest order went through has had that order's credit taken
  // off it. A success refetches it, so this fills in once that lands.
  const creditLeftCents =
    prepaidsQuery.data && lastOrderedAt !== null && prepaidsQuery.dataUpdatedAt >= lastOrderedAt
      ? prepaidRemainingCents(prepaidsQuery.data)
      : null;
  const orderedInvoices = React.useMemo(
    () =>
      orderedResults.flatMap((result) => {
        const hostedInvoiceUrl =
          result.outcome.kind === 'success'
            ? result.outcome.result.invoice?.hostedInvoiceUrl
            : null;
        return hostedInvoiceUrl
          ? [{key: result.groupKey, label: result.label, hostedInvoiceUrl}]
          : [];
      }),
    [orderedResults]
  );

  const browsePropertiesUrl = `/${orgIdPrefix}/projects`;

  if (orderedLines.length > 0 && items.length === 0) {
    // Property is offered only when a property holds more than one scene; below that the two views
    // differ only in which name headlines, since portfolio rows already name their property.
    const orderedPropertyGroupingIsAvailable = propertyGroupingAvailable(
      orderedLines.flatMap((line) => line.item ?? [])
    );
    // Property is the receipt's default whenever it groups something, since that's how the scenes
    // will arrive; only a portfolio view carried over from the cart overrides it. A `source` view
    // has no receipt equivalent and derives to the default, as an unusable value does in the cart.
    const orderedGroupBy: OrderedGroupBy =
      orderedPropertyGroupingIsAvailable && groupByParam !== 'portfolio' ? 'property' : 'portfolio';

    return (
      <CheckoutSuccess
        lines={orderedLines}
        totalCents={orderedTotals.cents}
        taxCents={orderedTotals.taxCents}
        totalBilledAcres={orderedTotals.acres}
        prepaidCents={orderedTotals.prepaidCents}
        creditLeftCents={creditLeftCents}
        invoices={orderedInvoices}
        userEmail={profile.get('email')}
        groupBy={orderedGroupBy}
        propertyGroupingIsAvailable={orderedPropertyGroupingIsAvailable}
        onGroupByChange={(next) => setGroupByParam(next, 'replaceIn')}
        browsePropertiesUrl={browsePropertiesUrl}
        billingUrl={`/${orgIdPrefix}/settings/billing`}
        propertyLinkFor={({item}) =>
          item ? cartItemPropertyLink(item, organizationId) : undefined
        }
      />
    );
  }

  return (
    <>
      {refreshFailed && (
        <div className={cs.refreshError}>
          <B.Callout intent={B.Intent.DANGER} icon="error" title="We couldn’t check every scene">
            Prices and availability may be out of date. Reload the page before ordering.
          </B.Callout>
        </div>
      )}
      <CartPageView
        displayGroups={displayGroups}
        groupBy={groupBy}
        propertyGroupingIsAvailable={propertyGroupingIsAvailable}
        onGroupByChange={(next) => setGroupByParam(next, 'replaceIn')}
        validations={validations}
        previews={previews}
        rejections={liveRejections}
        groupFailures={groupFailures(liveFailures)}
        contractGroupCount={contractGroups.length}
        nteScope={budgetLookup.budget ? 'organization' : 'contract'}
        unmatchedErrors={unmatchedErrors(liveFailures)}
        orderedSummary={
          orderedLines.length > 0
            ? {itemCount: orderedLines.length, totalCents: orderedTotals.cents}
            : null
        }
        fixPlan={fixPlan}
        itemCount={items.length}
        totalCents={cartTotalCents(items)}
        totalAcres={cartTotalAcres(items)}
        isSubmitting={submitting !== null}
        submitBlock={submitBlock}
        paymentSection={
          payNow ? (
            // Kept mounted, so a card typed in it survives an edit that sends the cart back to
            // checking its credit. Collapsed rather than hidden: Stripe's iframe may not initialize
            // under display: none.
            <div
              className={classnames({[cs.cardFormCollapsed]: !showsCardForm})}
              {...(showsCardForm ? {} : {...INERT, 'aria-hidden': true})}
            >
              <CartPaymentElement
                organizationId={organizationId}
                amountCents={
                  charges?.status === 'ready' ? charges.totalCents : cartTotalCents(items)
                }
                handleRef={paymentHandleRef}
                onStatusChange={setPaymentStatus}
              />
            </div>
          ) : null
        }
        billingNote={
          payNow
            ? {value: noteInUse, onChange: setBillingNote, isLocked: heldAttempt !== undefined}
            : null
        }
        isPayNow={payNow}
        paysWithCredit={paysWithCredit}
        charges={charges}
        usage={usageAndLimits(
          contractGroups,
          budgetLookup.budget,
          payNow ? (prepaidsQuery.data ?? null) : null
        )}
        paymentNotices={paymentNotices}
        browsePropertiesUrl={browsePropertiesUrl}
        deepLinkFor={(item) => cartItemDeepLink(item, organizationId)}
        propertyLinkFor={(item) => cartItemPropertyLink(item, organizationId)}
        onRemove={removeWithUndo}
        onRemoveBlocked={onRemoveBlocked}
        onAcceptRequote={onAcceptRequote}
        onApplyAllFixes={onApplyAllFixes}
        onClearCart={onClearCart}
        onSubmit={async () => {
          setSubmitting({plan, isFinishingOrder: false});

          let payment: Map<string, CheckoutAttempt> | undefined;
          if (payNow) {
            const note = noteInUse.trim();
            const attempts = new Map<string, CheckoutAttempt>();
            let tokenized = false;
            try {
              // One attempt per group: each group bills its own invoice, and a confirmation token
              // pays one invoice only.
              for (const group of contractGroups) {
                // A group whose charge hasn't settled is finished on the attempt it started, not a
                // new one — the server's single confirm is keyed on that pair, so a fresh token
                // here would be rejected and a fresh key would charge the card twice.
                const attempt = await submitAttempt(attemptStore.get(group.key), {
                  paysByCard: plan?.payments.get(group.key) !== 'credit',
                  note: note || undefined,
                  tokenize: async () => {
                    const result = await paymentHandleRef.current?.createConfirmationToken();
                    return result?.ok ? result.token.id : null;
                  },
                });
                // No token means the card needs fixing and the element is already saying so.
                // Nothing is ordered: every token has to exist before any purchase is created.
                if (!attempt) {
                  return;
                }
                attempts.set(group.key, attempt);
              }
              tokenized = true;
            } finally {
              // Nothing is going to the server, so there's no answer to hold the plan for.
              if (!tokenized) {
                setSubmitting(null);
              }
            }
            // Recorded only now that every group has one, so a token minted for a submit that
            // never went out can't be replayed against a card the user has since changed.
            for (const [groupKey, attempt] of attempts) {
              attemptStore.set(groupKey, attempt);
            }
            payment = attempts;
          }

          // Cleared only once a request is going out: until then the last answer still stands, and
          // the card it asked for is read from it.
          setFailedResults([]);
          setRejections(new Map());
          setPaymentNotices([]);
          submitMutation.mutate({groups: contractGroups, payment});
        }}
      />
    </>
  );
};

export default CartPage;
